// Privacy policy v1.0 (effective 1 September 2026), served until v1.1 takes effect on 1 November 2026
// (src/content/legal/index.ts getGdprDoc). Delete this folder after that date. Amended in place on 22 October 2026
// (owner decision, as it is the live version): §4's legal basis, §5's face-search bullet and §13 now carry the face
// data policy's Studio §5 rule, in every language, the same as v1.1.
import type { LegalDoc } from "../types";

// English master. Keep this and the translations (gdpr.<lang>.ts) in sync; this is
// the source of truth. Mirrors the reviewed policy (v1.0, effective 1 Sept 2026).
const gdprEn: LegalDoc = {
  title: "Privacy & Data Protection Policy (GDPR / RGPD)",
  effectiveDate: "1 September 2026",
  version: "1.0",
  intro:
    "This policy explains how Photohawk handles personal data under the EU General Data Protection Regulation (GDPR) and its French equivalent, the RGPD (Règlement Général sur la Protection des Données), as well as the UK GDPR. RGPD and GDPR are the same law.",
  sections: [
    {
      heading: "1. Who we are",
      blocks: [
        {
          type: "p",
          text: "Photohawk is an event-photography platform that lets photographers and organisers publish, search and sell event photos.",
        },
        {
          type: "ul",
          items: [
            "Legal entity: CLOUDCONCUR LTD (company number 13399913), a company registered in England & Wales, trading as Photohawk.",
            "Registered address: 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
            "Data Protection Officer (DPO): Andrew Hammond — contact support@photohawk.com.",
            "EU representative (GDPR Art. 27): we have not appointed an EU representative, on the basis that our processing of EU residents' data is occasional and not our main activity (Art. 27(2)). We keep this under review and will appoint a representative if our processing changes.",
            "Supervisory authorities: the UK Information Commissioner's Office (ICO) for the UK, and EU authorities including the French CNIL for individuals in the EU.",
          ],
        },
      ],
    },
    {
      heading: "2. Our two roles — controller and processor",
      blocks: [
        {
          type: "p",
          text: "Photohawk deals with two kinds of people, and our legal role differs for each:",
        },
        {
          type: "ul",
          items: [
            "Photographers and organisers (our customers). For your account data (name, email, business details, login, billing and payout information) Photohawk is the data controller.",
            "Event attendees and buyers (your customers). For the data of the people in and buying the photos, you (the photographer/organiser) are the data controller and Photohawk is your data processor — we process that data on your instructions to run the galleries, search and sales you have set up. A Data Processing Agreement (see §11) governs this relationship.",
          ],
        },
      ],
    },
    {
      heading: "3. What personal data we process",
      blocks: [
        { type: "p", text: "For photographers/organisers (we are controller):" },
        {
          type: "ul",
          items: [
            "Identity and contact: name, email, phone, business name, address.",
            "Account & security: login credentials (via AWS), sessions.",
            "Financial: subscription/billing data (via Stripe) and payout details (via Trolley).",
          ],
        },
        { type: "p", text: "For event attendees/buyers (we are processor, on the organiser's behalf):" },
        {
          type: "ul",
          items: [
            "Identity and contact: name, email, phone number, postal/billing address.",
            "Photographs of attendees.",
            "Biometric data — where face/selfie search is enabled (see §4).",
            "Order and payment data (processed via our payment providers; we do not store full card numbers).",
            "Marketing consent status, where the attendee opts in.",
          ],
        },
      ],
    },
    {
      heading: "4. Face and selfie search (special-category biometric data)",
      blocks: [
        {
          type: "p",
          text: "Where an organiser enables selfie/face search, Photohawk creates a mathematical representation of faces so an attendee can find their own photos.",
        },
        {
          type: "ul",
          items: [
            "Purpose: solely to match a person to their own event photos.",
            "Where: processed and stored in the European Union (AWS, Ireland region).",
            "Retention: face/biometric data is automatically deleted when a gallery ages out (within ~60 days) and when a gallery is deleted — whichever comes first.",
            "Legal basis (GDPR Art. 9): biometric data is a special category and gets extra protection: it is used only so that people can find their own photos in that gallery. As the data controller for attendees, the gallery's administrator decides whether face search is appropriate for their event and permitted where it takes place. Where local law requires the consent of the people photographed, the administrator must have it, for example through event registration; where they don't, they should use bib and time search instead, which uses no face data. Photohawk provides an in-product consent step: an attendee is asked to consent before their selfie is used for face search, and can decline.",
            "Minors: see §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Why we use data and our legal bases",
      blocks: [
        {
          type: "ul",
          items: [
            "To provide the service (host galleries, run search, process orders): performance of a contract.",
            "Payments, payouts, tax and accounting records: performance of a contract and compliance with a legal obligation.",
            "Face/selfie search (Art. 9): the explicit consent of the person searching, before their selfie is used; for the people photographed, as described in §4.",
            "Marketing emails/messages: only with the recipient's consent (opt-in), which can be withdrawn at any time.",
            "Analytics and advertising cookies: only with your consent, given via our cookie banner (see §12).",
            "Security, fraud prevention and service improvement: our legitimate interests, balanced against your rights.",
          ],
        },
      ],
    },
    {
      heading: "6. Where your data is stored and international transfers",
      blocks: [
        {
          type: "ul",
          items: [
            "Data residency: attendee and account data — including photos and biometric face data — is hosted in the European Union (AWS, Ireland / eu-west-1). Our identity service (AWS) is also in the EU.",
            "International transfers: some of our sub-processors (see §7) operate outside the EU/UK (e.g. in the United States). Where personal data is transferred outside the EU/UK, we rely on an adequacy decision where one applies, and otherwise on the European Commission's Standard Contractual Clauses (SCCs) together with the UK International Data Transfer Addendum, plus additional safeguards where needed.",
          ],
        },
      ],
    },
    {
      heading: "7. Sub-processors",
      blocks: [
        {
          type: "p",
          text: "We use a small number of vetted providers to deliver the service, each under a data-processing agreement (and SCCs/UK Addendum where they process data outside the EU/UK):",
        },
        {
          type: "table",
          head: ["Provider", "Purpose", "Data involved"],
          rows: [
            ["Amazon Web Services (AWS)", "Hosting, storage, identity, face search, transactional email — EU region", "All personal data, photos, biometric data"],
            ["Cloudflare (incl. R2)", "CDN, custom domains, object storage", "Photos, request data"],
            ["Stripe", "Card payments & subscriptions", "Buyer & customer payment data"],
            ["Xendit", "Payments (Southeast Asia)", "Buyer payment data"],
            ["Paystack", "Payments (Africa)", "Buyer payment data"],
            ["Trolley", "Photographer payouts", "Payee identity & bank details"],
            ["Postmark", "Transactional email", "Names, email addresses"],
            ["Twilio / WhatsApp", "Photo delivery & notifications", "Names, phone numbers"],
            ["Google (Google Analytics)", "Website usage analytics (consent-based)", "Usage/device data, identifiers"],
            ["Meta Platforms (Meta Pixel)", "Advertising & conversion measurement (consent-based)", "Usage data, identifiers"],
          ],
        },
        { type: "p", text: "A current list is available on request." },
      ],
    },
    {
      heading: "8. How long we keep data (retention)",
      blocks: [
        {
          type: "ul",
          items: [
            "Face / biometric data: deleted within ~60 days of a gallery ageing out, and on gallery deletion (see §4).",
            "Photos and videos: retention is controlled by the account owner (the organiser/photographer). Content is kept for as long as the account owner keeps it; account owners can delete photos, videos, galleries and attendee records at any time, and we delete it when they do. Photohawk does not impose a fixed lifetime on this content.",
            "Orders and financial records: kept for 7 years to meet tax and accounting obligations.",
            "Attendee/buyer contact records: controlled by the organiser — kept while the organiser keeps them, and deleted when the organiser deletes them or on a verified erasure request.",
            "Marketing lists: kept until consent is withdrawn.",
            "Operational/security logs are retained for 30 days.",
          ],
        },
      ],
    },
    {
      heading: "9. How we protect data",
      blocks: [
        {
          type: "ul",
          items: [
            "In transit: encrypted over HTTPS/TLS everywhere.",
            "At rest: photos and content are stored on AWS and Cloudflare R2, which encrypt data at rest by default; sensitive credentials (e.g. connected-account tokens) are additionally encrypted with AWS.",
            "Access controls: authenticated access via AWS; staff access is limited to what is needed to operate the service.",
          ],
        },
      ],
    },
    {
      heading: "10. Your rights",
      blocks: [
        {
          type: "p",
          text: "Under the GDPR/RGPD and UK GDPR you have the right to: access your data; rectify it; erase it (“right to be forgotten”); restrict or object to processing; data portability; and to withdraw consent at any time. You also have the right to complain to a supervisory authority (the ICO in the UK, or your local authority such as the CNIL in France).",
        },
        { type: "p", text: "How to exercise them:" },
        {
          type: "ul",
          items: [
            "Photographers/organisers (we are controller): contact our DPO at support@photohawk.com. We respond within one month.",
            "Event attendees/buyers (the organiser is controller): please contact the photographer or organiser who ran your event. Photographers can delete an attendee's personal data — including their photos and any face-search data — from within Photohawk, and Photohawk will action a verified request on the organiser's behalf.",
          ],
        },
        {
          type: "p",
          text: "Closing a Photohawk account cancels billing but does not by itself erase stored data; ask us to erase data if that is what you need.",
        },
      ],
    },
    {
      heading: "11. Data Processing Agreement (DPA)",
      blocks: [
        {
          type: "p",
          text: "For photographers/organisers, our processing of your attendees' data is governed by a Data Processing Agreement under GDPR Art. 28, which we make available on request at support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Cookies and similar technologies",
      blocks: [
        {
          type: "ul",
          items: [
            "Strictly necessary cookies (for login, security and core functionality) are always active; they don't require consent.",
            "Analytics and advertising cookies — Google Analytics and the Meta Pixel (see §7) — are non-essential and load only after you opt in through our cookie consent banner. You can change or withdraw your choice at any time via the banner / cookie settings. Declining leaves the service fully usable.",
            "More detail is set out in our separate Cookie Policy.",
          ],
        },
      ],
    },
    {
      heading: "13. Children's data",
      blocks: [
        {
          type: "p",
          text: "Events may include minors. Whether face search is appropriate for an event is the gallery's administrator's decision (see §4); where minors may be present and local law requires the consent of the people photographed, the administrator must have the consent of a parent or guardian for them, or use bib and time search instead. Photohawk does not knowingly create biometric data for a child without a consent the law requires, and the in-product consent step reinforces this.",
        },
      ],
    },
    {
      heading: "14. Data breaches",
      blocks: [
        {
          type: "p",
          text: "If a personal-data breach occurs, Photohawk will notify the affected data controller without undue delay after becoming aware of it, and will assist them in meeting their own notification obligations to supervisory authorities and individuals (GDPR Arts. 33–34).",
        },
      ],
    },
    {
      heading: "15. Changes to this policy",
      blocks: [
        {
          type: "p",
          text: "We may update this policy; the “Effective date” above shows the current version. Material changes will be communicated to account holders.",
        },
      ],
    },
    {
      heading: "16. Contact",
      blocks: [
        {
          type: "p",
          text: "Data Protection Officer — Andrew Hammond. CLOUDCONCUR LTD (company no. 13399913), trading as Photohawk. Email: support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, United Kingdom.",
        },
      ],
    },
  ],
};

export default gdprEn;
